/* eslint-disable camelcase */

import CustomIntercomLauncher from '@components/CustomIntercomLauncher';
import FrontendV2Redirector from '@components/FrontendV2Redirector';
import GlobalUIWrapper from '@components/GlobalUIWrapper/index';
import loadable from '@components/loadable';
import { datadogRum } from '@datadog/browser-rum';
import config from '@lib/config';
import * as Intercom from '@lib/intercom';
import * as logger from '@lib/logger';
import { Toaster, TooltipProvider } from '@retracedgmbh/ui-components-lib';
import { useAuth } from '@state/auth';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { checkBrowserSupport } from '@utils';
import { isEmpty } from 'lodash-es';
import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@/hooks/useApi';
import './services/i18n';
import { version } from '../package.json';

const { VITE_RETRACED_ENV, VITE_ENVIRONMENT } = import.meta.env;

import { routerConfig } from './router/router-config';

Intercom.init();

if (VITE_ENVIRONMENT !== 'development' && VITE_ENVIRONMENT !== 'staging') {
  logger.init();
}

if (config.environment !== 'development') {
  datadogRum.init({
    allowedTracingUrls: [/https:\/\/(?!auth\.).*\.retraced\.com.+/], // trace all requests to retraced.com except auth.retraced.com
    applicationId: '057c75e5-bb0f-48a2-a228-c4bca2c782d9',
    clientToken: 'pub412d965c8ad6bab81dd2bcd7eea26b83',
    site: 'datadoghq.eu',
    service: 'dashboard-web',
    env: VITE_RETRACED_ENV,
    version: `v${version}`,
    sampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    defaultPrivacyLevel: 'allow',
    enableExperimentalFeatures: ['feature_flags'],
    beforeSend: async (event, context) => {
      // Map context.team to codeowners.team for aggregation with backend logs
      if (context.team) {
        event.context = event.context || {};
        event.context['codeowners.team'] = context.team;
      }

      if (event.type === 'resource' && event.resource.type === 'fetch') {
        const response = context.response;
        if (response?.body) {
          if (!response.body.locked) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let accumulatedData = '';
            const readChunk = async () => {
              const { value, done } = await reader.read();
              if (done) {
                return;
              }
              accumulatedData += decoder.decode(value);
              await readChunk();
            };
            await readChunk();
            try {
              event.context.responseBody = JSON.parse(accumulatedData);
            } catch (_e) {
              event.context.responseBody = JSON.parse('{}');
            }
          }
        }
      }
      return true;
    },
  });
}

const UnSupportedBrowser = loadable(() => import('@views/unsuported-browser'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const router = React.useMemo(() => createBrowserRouter(routerConfig), []);

  const isBrowserSupported = React.useMemo(checkBrowserSupport, []);
  if (!isBrowserSupported) return <UnSupportedBrowser />;

  const { me, companyId, impersonateId } = useAuth(state => ({
    me: state.me,
    companyId: state.companyId,
    impersonateId: state.impersonateId,
  }));

  if (!isEmpty(me)) {
    datadogRum.setUser({
      id: me?.unImpersonatedUser?.id || me?.id,
      email: me?.unImpersonatedUser?.email || me?.email,
      companyId: me?.unImpersonatedUser?.companyId || me?.companyId,
      delegatedCompanyId: companyId,
      impersonateId: impersonateId,
    });
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <GlobalUIWrapper>
          <FrontendV2Redirector>
            <Toaster />
            <RouterProvider
              router={router}
              future={{
                v7_relativeSplatPath: true,
                v7_startTransition: true,
              }}
            />
            <CustomIntercomLauncher />
          </FrontendV2Redirector>
        </GlobalUIWrapper>
      </TooltipProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
